// src/components/common/LegalPage/LegalPage.jsx
//
// ОБЩИЙ КАРКАС ДЛЯ ЮРИДИЧЕСКИХ СТРАНИЦ (политика, условия, возврат).
//
// ⚠️ ЭТО ЧЕРНОВИКИ. Владелец (2026-10-07): «Юридические тексты напиши
//    пробные сам, я потом сам их исправлю».
//
// 🔴 ВАЖНО ДЛЯ ТОГО, КТО БУДЕТ ПРАВИТЬ ТЕКСТ:
//    Тексты ниже — СТРУКТУРА и ОРИЕНТИР, а не готовая юридическая защита.
//    Перед публикацией их должен проверить юрист. Все места, где нужны
//    реальные данные компании, помечены `[квадратными скобками]` —
//    их НАДО заменить, они видны на странице намеренно.
//
// ⚠️ НЕ ВЫДУМЫВАЙ реквизиты. Если данных нет — оставляй заглушку
//    `[ИНН]`. Выдуманный ИНН в юридическом документе хуже пустого места:
//    он выглядит как настоящий.
//
// 📌 ВЕРСИЯ ДОКУМЕНТА. Константа `DOCUMENT_VERSION` должна совпадать с
//    версией, которая пишется в БД при фиксации согласия
//    (`consent_records.document_version`, см. NORRAN_INTEGRATION.txt).
//    Меняя текст содержательно — ПОДНИМИ версию, иначе нельзя будет
//    ответить, на что именно человек согласился.
import React from 'react';
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs.jsx';

/**
 * Версия редакции юридических документов.
 *
 * ⚠️ Поднимай при СОДЕРЖАТЕЛЬНОЙ правке (изменилось обязательство, а не
 *    опечатка). Эта же версия уходит в `consent_records.document_version`.
 */
export const DOCUMENT_VERSION = '1.0';

/** Дата последней редакции — показывается читателю. */
const LAST_UPDATED = '07.10.2026';

export const LegalPage = ({ title, intro, children }) => {
  const breadcrumbs = [{ label: title, path: '#' }];

  return (
    <div style={{ backgroundColor: 'rgb(var(--c-bg))', minHeight: '100vh', paddingBottom: '60px' }}>
      <div className="container" style={{ paddingTop: '24px' }}>
        <Breadcrumbs items={breadcrumbs} />

        <h1
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: 'rgb(var(--c-text))',
            marginTop: '24px',
            marginBottom: '8px',
          }}
        >
          {title}
        </h1>

        <p style={{ fontSize: '14px', color: '#9B9B9B', marginBottom: '24px' }}>
          Редакция {DOCUMENT_VERSION} от {LAST_UPDATED}
        </p>

        {/* ⚠️ Предупреждение о черновике. УБРАТЬ после проверки юристом. */}
        <div
          style={{
            backgroundColor: '#FFF8E1',
            border: '1px solid #FFE082',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '28px',
          }}
        >
          <p style={{ fontSize: '14px', color: '#7A5C00', margin: 0, lineHeight: 1.6 }}>
            <strong>Черновик.</strong> Текст требует проверки юристом и
            заполнения реквизитов — места в квадратных скобках нужно
            заменить на реальные данные.
          </p>
        </div>

        {intro && (
          <p
            style={{
              fontSize: '16px',
              color: 'rgb(var(--c-text-secondary))',
              lineHeight: 1.8,
              marginBottom: '28px',
            }}
          >
            {intro}
          </p>
        )}

        <div className="legal-content">{children}</div>
      </div>
    </div>
  );
};

/**
 * Раздел документа.
 *
 * ⚠️ Нумерация задаётся ЯВНО (`number`), а не автогенерируется: на разделы
 *    ссылаются (например, «п. 5.2 настоящей Политики»), и автонумерация
 *    молча сдвинула бы все ссылки при вставке нового раздела.
 */
export const LegalSection = ({ number, title, children }) => (
  <section style={{ marginBottom: '28px' }}>
    <h2
      style={{
        fontSize: '20px',
        fontWeight: 600,
        color: 'rgb(var(--c-text))',
        marginBottom: '12px',
        lineHeight: 1.4,
      }}
    >
      {number}. {title}
    </h2>
    <div style={{ fontSize: '15px', color: 'rgb(var(--c-text-secondary))', lineHeight: 1.8 }}>
      {children}
    </div>
  </section>
);

/** Маркированный список — единый вид во всех юридических страницах. */
export const LegalList = ({ items }) => (
  <ul style={{ paddingLeft: '22px', margin: '10px 0' }}>
    {items.map((item, i) => (
      <li key={i} style={{ marginBottom: '6px' }}>
        {item}
      </li>
    ))}
  </ul>
);

/**
 * Заглушка под реальные данные компании.
 *
 * ⚠️ Видна на странице НАМЕРЕННО (жёлтая подсветка), чтобы незаполненное
 *    место нельзя было не заметить. Заменив на реальные данные — просто
 *    используй текст вместо этого компонента.
 */
export const Placeholder = ({ children }) => (
  <span
    style={{
      backgroundColor: 'rgb(var(--c-warning-bg))',
      borderBottom: '1px dashed #D9A400',
      padding: '0 4px',
      borderRadius: '3px',
      color: '#7A5C00',
      fontStyle: 'italic',
    }}
  >
    {children}
  </span>
);
